import { Box, Button, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { gateway } from "../gateway";
import Confetti from "react-confetti";
import { CheckCircle, ArrowForward, ErrorOutline } from "@mui/icons-material";
import { colors } from "./ui/colors";

export const EmailConfirmed = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isActivated, setIsActivated] = useState(false);
  const [error, setError] = useState(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const { pathname } = useLocation();
  const [base, path, subPath, uidToken] = pathname.split("/");
  const navigate = useNavigate();

  const confirmEmail = async () => {
    setIsLoading(true);

    try {
      const response = await gateway.get(`/users/activation/${uidToken}`);
      if (response.status === 200) {
        setIsActivated(true);
      } else if (response.status === 401) {
        setError("Invalid activation link. Please try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error in activation request", error);
      setError("Failed to activate account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    confirmEmail();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
          background: `
            linear-gradient(135deg, 
              rgba(255, 152, 0, 0.08) 0%, 
              rgba(255, 193, 7, 0.06) 50%,
              #fafafa 100%
            )
          `,
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            p: 6,
            borderRadius: "20px",
            backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.9)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              border: `4px solid transparent`,
              background: `
                linear-gradient(white, white) padding-box,
                linear-gradient(45deg, ${colors.orange.base}, ${colors.yellow.base}) border-box
              `,
              margin: "0 auto 24px",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "24px",
                height: "24px",
                border: `3px solid transparent`,
                borderTop: `3px solid ${colors.orange.base}`,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              },
              "@keyframes spin": {
                "0%": { transform: "translate(-50%, -50%) rotate(0deg)" },
                "100%": { transform: "translate(-50%, -50%) rotate(360deg)" },
              },
            }}
          />
          <Typography
            sx={{
              color: colors.gray[700],
              fontSize: "18px",
              fontWeight: 600,
              mb: 1,
            }}
          >
            Activating Your Account
          </Typography>
          <Typography
            sx={{
              color: colors.gray[500],
              fontSize: "14px",
            }}
          >
            Please wait a moment...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        background: isActivated
          ? `
              radial-gradient(800px 600px at 30% -10%, rgba(255, 152, 0, 0.08) 0%, transparent 50%),
              radial-gradient(600px 400px at 70% 110%, rgba(255, 193, 7, 0.06) 0%, transparent 50%),
              linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)
            `
          : `
              radial-gradient(800px 600px at 30% -10%, rgba(244, 67, 54, 0.08) 0%, transparent 50%),
              radial-gradient(600px 400px at 70% 110%, rgba(239, 83, 80, 0.06) 0%, transparent 50%),
              linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)
            `,
      }}
    >
      {isActivated && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          numberOfPieces={120}
          recycle={false}
          gravity={0.25}
          colors={[
            colors.orange[300],
            colors.orange.base,
            colors.yellow[400],
            colors.yellow.base,
          ]}
        />
      )}

      <Box
        sx={{
          width: "100%",
          maxWidth: 520,
          borderRadius: "24px",
          background: `
            linear-gradient(135deg, 
              rgba(255, 255, 255, 0.95) 0%, 
              rgba(255, 255, 255, 0.85) 100%
            )
          `,
          backdropFilter: "blur(20px)",
          p: { xs: 5, md: 7 },
          textAlign: "center",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            mb: 4,
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isActivated
                ? `linear-gradient(135deg, ${colors.orange.base} 0%, ${colors.yellow.base} 100%)`
                : "linear-gradient(135deg, #F44336 0%, #EF5350 100%)",
              boxShadow: isActivated
                ? `0 15px 35px rgba(255, 152, 0, 0.4),
                   0 5px 15px rgba(255, 193, 7, 0.3)`
                : `0 15px 35px rgba(244, 67, 54, 0.4),
                   0 5px 15px rgba(239, 83, 80, 0.3)`,
            }}
          >
            {isActivated ? (
              <CheckCircle
                sx={{
                  fontSize: 50,
                  color: "white",
                }}
              />
            ) : (
              <ErrorOutline
                sx={{
                  fontSize: 50,
                  color: "white",
                }}
              />
            )}
          </Box>
        </Box>

        {isActivated ? (
          <>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "32px", md: "40px" },
                mb: 2,
                background: `linear-gradient(135deg, ${colors.orange.base}, ${colors.yellow.base})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Welcome! 🎉
            </Typography>

            <Box sx={{ mb: 5 }}>
              <Typography
                sx={{
                  color: colors.gray[700],
                  fontSize: { xs: "18px", md: "20px" },
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Your email has been verified successfully!
              </Typography>
              <Typography
                sx={{
                  color: colors.gray[600],
                  fontSize: { xs: "16px", md: "17px" },
                  fontWeight: 500,
                }}
              >
                You're all set to start using{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${colors.orange.base}, ${colors.yellow.base})`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  Flownect
                </Box>
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/signin")}
              sx={{
                borderRadius: "12px",
                height: "56px",
                px: 6,
                background: "#000000",
                color: "white",
                fontWeight: 700,
                
                fontSize: "17px",
                textTransform: "none",
                "&:hover": {
                  background: "#000000",
                },
              }}
              variant="contained"
              disableElevation
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                Continue to Sign In
                <ArrowForward sx={{ fontSize: 20 }} />
              </Box>
            </Button>
          </>
        ) : (
          <>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "32px", md: "40px" },
                mb: 2,
                background: "linear-gradient(135deg, #F44336 0%, #EF5350 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Oops! Something Went Wrong
            </Typography>

            <Box sx={{ mb: 5 }}>
              <Typography
                sx={{
                  color: colors.gray[700],
                  fontSize: { xs: "18px", md: "20px" },
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                {error || "We couldn't activate your account"}
              </Typography>
              <Typography
                sx={{
                  color: colors.gray[600],
                  fontSize: { xs: "16px", md: "17px" },
                  fontWeight: 500,
                }}
              >
                Don't worry! You can try creating a new account or contact support.
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/signup")}
              sx={{
                borderRadius: "12px",
                height: "56px",
                px: 6,
                background: "linear-gradient(135deg, #F44336 0%, #EF5350 100%)",
                color: "white",
                fontWeight: 700,
                fontSize: "17px",
                textTransform: "none",
              }}
              variant="contained"
              disableElevation
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                Try Again
                <ArrowForward sx={{ fontSize: 20 }} />
              </Box>
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};
